import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

function StatBox({ value, label }) {
  return (
    <div className="login-stat">
      <div className="login-stat-val">{value}</div>
      <div className="login-stat-label">{label}</div>
    </div>
  );
}

export default function LoginPage() {
  const { login, loginWithOtp, user } = useAuth();
  const navigate = useNavigate();

  // 'email' | 'password' | 'otp'
  const [step, setStep]         = useState('email');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp]           = useState('');
  const [accountType, setAccountType] = useState('staff');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  // Resend cooldown
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef(null);

  useEffect(() => () => clearInterval(cooldownRef.current), []);

  if (user) return <Navigate to={user.role === 'student' ? '/my-practicum' : '/'} />;

  // ── Step 1: check email → password or OTP ──────────────────────────────────
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (accountType === 'staff') {
        setStep('password');
      } else {
        await api.post('/auth/otp/request', { email });
        setStep('otp');
        startCooldown();
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2a: staff password login ─────────────────────────────────────────
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setError('');
    setResetLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setResetSent(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not send the reset email. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  // ── Step 2b: student OTP verify ───────────────────────────────────────────
  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const u = await loginWithOtp(email, otp);
      navigate(u.role === 'student' ? '/my-practicum' : '/');
    } catch (err) {
      const msg = err.response?.data?.error;
      setError(msg === 'invalid_otp' ? 'Invalid or expired code. Please try again.' : 'Verification failed.');
    } finally {
      setLoading(false);
    }
  };

  // ── Resend OTP ─────────────────────────────────────────────────────────────
  const startCooldown = () => {
    setResendCooldown(30);
    clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(cooldownRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setError('');
    try {
      await api.post('/auth/otp/request', { email });
      startCooldown();
    } catch {
      setError('Failed to resend code.');
    }
  };

  const goBack = () => {
    setStep('email');
    setPassword('');
    setOtp('');
    setError('');
  };

  return (
    <div className="login-split">

      {/* ── Left: Brand panel ── */}
      <div className="login-brand">
        <svg className="login-brand-rings" viewBox="0 0 560 560" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <circle cx="420" cy="140" r="220" stroke="rgba(255,255,255,0.07)" strokeWidth="1.5" />
          <circle cx="420" cy="140" r="310" stroke="rgba(255,255,255,0.05)" strokeWidth="1.5" />
          <circle cx="420" cy="140" r="400" stroke="rgba(222,152,66,0.08)" strokeWidth="1.5" />
        </svg>
        <div className="login-brand-logo">
          <img src="/mcg-logo.png" alt="MCG Career College" className="login-brand-lockup" />
        </div>
        <div className="login-brand-body">
          <div className="login-brand-eyebrow">Career Services · Practicum Portal</div>
          <h1 className="login-brand-headline">
            Every student.<br />
            Every hour.<br />
            <span className="login-brand-coral">In one place.</span>
          </h1>
          <p className="login-brand-copy">
            Track placements, surface bottlenecks, and keep your cohort
            moving — from compliance to grade release.
          </p>
          <div className="login-stats-row">
            <StatBox value="430" label="Host partners" />
            <div className="login-stat-divider" />
            <StatBox value="5" label="Active programs" />
            <div className="login-stat-divider" />
            <StatBox value="98%" label="On-time placement" />
          </div>
        </div>
      </div>

      {/* ── Right: Form panel ── */}
      <div className="login-form-panel">
        <div className="login-form-inner">

          {/* ── STEP: email ── */}
          {step === 'email' && (
            <>
              <h2 className="login-heading">Welcome back</h2>
              <p className="login-desc">Sign in to your MCG Career College account</p>
              {error && <div className="login-error">{error}</div>}
              <form onSubmit={handleEmailSubmit} className="login-form">
                <div className="login-field">
                  <label className="login-label">Email address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="login-input"
                    placeholder="you@mcgcollege.com"
                    autoComplete="email"
                    autoFocus
                    required
                  />
                </div>
                <div className="login-field">
                  <label className="login-label">Account type</label>
                  <select
                    value={accountType}
                    onChange={e => setAccountType(e.target.value)}
                    className="login-input"
                  >
                    <option value="staff">Staff</option>
                    <option value="student">Student</option>
                  </select>
                </div>
                <button type="submit" disabled={loading} className="login-btn">
                  {loading ? 'Checking…' : <>Continue &nbsp;→</>}
                </button>
              </form>
              <div className="login-or"><span>OR CONTINUE WITH</span></div>
              <div className="login-social">
                <button type="button" className="login-social-btn">
                  <svg width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                  Google
                </button>
                <button type="button" className="login-social-btn">
                  <svg width="18" height="18" viewBox="0 0 24 24"><path d="M11.4 24H0V12.6L11.4 24z" fill="#F25022"/><path d="M24 24H12.6L24 12.6V24z" fill="#7FBA00"/><path d="M11.4 0H0v11.4L11.4 0z" fill="#00A4EF"/><path d="M24 0H12.6L24 11.4V0z" fill="#FFB900"/></svg>
                  Microsoft
                </button>
              </div>
              <p className="login-request">
                New to MCG Career Services?{' '}
                <button type="button" className="login-request-link">Request access</button>
              </p>
            </>
          )}

          {/* ── STEP: password (staff) ── */}
          {step === 'password' && (
            <>
              <h2 className="login-heading">Welcome back</h2>
              <p className="login-desc">Sign in with your staff password</p>
              {error && <div className="login-error">{error}</div>}
              <div className="login-email-chip">
                <span>{email}</span>
                <button type="button" onClick={goBack} className="login-change-link">Change</button>
              </div>
              <form onSubmit={handlePasswordSubmit} className="login-form">
                <div className="login-field">
                  <div className="login-label-row">
                    <label className="login-label">Password</label>
                    <button type="button" className="login-forgot" onClick={handleForgotPassword} disabled={resetLoading}>
                      {resetLoading ? 'Sending…' : 'Forgot password?'}
                    </button>
                  </div>
                  <input
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="login-input"
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    autoFocus
                    required
                  />
                </div>
                <button type="submit" disabled={loading} className="login-btn">
                  {loading ? 'Signing in…' : <>Sign in &nbsp;→</>}
                </button>
              </form>
              {resetSent && (
                <div className="login-error" style={{ color: '#166534', background: '#f0fdf4', borderColor: '#bbf7d0' }}>
                  If an active staff account exists for this email, a reset link has been sent. Check your inbox and junk folder.
                </div>
              )}
            </>
          )}

          {/* ── STEP: OTP (student) ── */}
          {step === 'otp' && (
            <>
              <h2 className="login-heading">Check your email</h2>
              <p className="login-desc">
                We sent a 6-digit code to <strong>{email}</strong>. Enter it below to sign in.
              </p>
              {error && <div className="login-error">{error}</div>}
              <div className="login-email-chip">
                <span>{email}</span>
                <button type="button" onClick={goBack} className="login-change-link">Change</button>
              </div>
              <form onSubmit={handleOtpSubmit} className="login-form">
                <div className="login-field">
                  <label className="login-label">6-digit code</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    value={otp}
                    onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="login-input login-otp-input"
                    placeholder="000000"
                    autoFocus
                    required
                  />
                </div>
                <button type="submit" disabled={loading || otp.length !== 6} className="login-btn">
                  {loading ? 'Verifying…' : <>Verify code &nbsp;→</>}
                </button>
              </form>
              <p className="login-resend">
                Didn't receive it?{' '}
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resendCooldown > 0}
                  className="login-request-link"
                  style={{ opacity: resendCooldown > 0 ? 0.5 : 1 }}
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                </button>
              </p>
            </>
          )}
        </div>

        <footer className="login-footer">
          © {new Date().getFullYear()} MCG Career College
          <span className="login-footer-dot">·</span>
          <button type="button" className="login-footer-link">Privacy</button>
          <span className="login-footer-dot">·</span>
          <button type="button" className="login-footer-link">Terms</button>
          <span className="login-footer-dot">·</span>
          <button type="button" className="login-footer-link">Support</button>
        </footer>
      </div>
    </div>
  );
}
